/* PartnerEcosystemPanel.jsx — Multi-party operational partner intelligence.
 *
 * Surfaces the ecosystem of solicitors, brokers, surveyors, conveyancers
 * and other third-party operators on the office's transactions. Composes
 * responsiveness, reliability, recovery participation and velocity impact
 * into a single composite score per partner organisation.
 *
 * Aesthetic
 *   • Strict gp-card glassmorphism reuse · cinematic dark hero
 *   • Calm operational palette · no marketplace styling · no social UI
 *   • Linkable per-partner deep dive via /partners
 *
 * Data
 *   GET /api/partners/ecosystem/overview → payload.ecosystem
 *   Leadership-only · returns 403 otherwise.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Network, Users, Gauge, AlertOctagon, Trophy, ShieldCheck,
  Scale, Hammer, FileCheck, BadgeCheck, Truck, Wrench, Building2,
  ArrowUpRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { MissionPanelSkeleton } from "../ui/operational-skeleton";

const ICON_MAP = {
  Scale, ShieldCheck, Hammer, FileCheck, BadgeCheck, Truck, Wrench, Building2,
};

const BAND_TONE = {
  Strong:    { fg: "#047857", bg: "rgba(16,185,129,0.14)",  dot: "#10B981" },
  Stable:    { fg: "#4338CA", bg: "rgba(99,102,241,0.10)",  dot: "#818CF8" },
  Watching:  { fg: "#92400E", bg: "rgba(245,158,11,0.12)",  dot: "#F59E0B" },
  Developing:{ fg: "#9F1239", bg: "rgba(244,63,94,0.10)",   dot: "#F43F5E" },
};

const PartnerIcon = ({ name, size = 12, ...rest }) => {
  const Cmp = ICON_MAP[name] || Users;
  return <Cmp size={size} strokeWidth={2.3} {...rest} />;
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const EcosystemHero = ({ summary, narrative }) => (
  <div
    className="relative overflow-hidden rounded-2xl px-5 sm:px-6 py-5 mb-5"
    data-testid="ecosystem-hero"
    style={{
      background: "linear-gradient(135deg, #050818 0%, #181E48 55%, #2D2C7A 100%)",
      boxShadow: "0 24px 60px -32px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-12 -left-8 w-[260px] h-[260px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(99,102,241,0.32), transparent 65%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-2">
        <Network size={12} className="text-indigo-300" strokeWidth={2.3} />
        <p className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-indigo-200/90">
          Partner Ecosystem
        </p>
      </div>
      <p className="text-[13px] sm:text-[13.5px] text-white/85 leading-relaxed max-w-2xl mb-4"
         data-testid="ecosystem-narrative">
        {narrative}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat label="Partners"    value={summary?.partner_count ?? 0}                  accent="#818CF8" testid="eco-stat-count" />
        <Stat label="Types"       value={summary?.type_count ?? 0}                     accent="#A78BFA" testid="eco-stat-types" />
        <Stat label="Responsiveness" value={`${summary?.ecosystem_responsiveness ?? 0}/100`} accent="#10B981" testid="eco-stat-responsiveness" />
        <Stat label="For attention"  value={summary?.bottleneck_count ?? 0}            accent="#F43F5E" testid="eco-stat-bottlenecks" />
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div
    className="rounded-lg px-3 py-2.5"
    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
    data-testid={testid}
  >
    <p className="text-[9px] uppercase tracking-[0.16em] font-bold text-white/55">{label}</p>
    <p className="text-[16px] font-semibold tabular-nums" style={{ color: accent }}>{value}</p>
  </div>
);

/* ─── Partner row ──────────────────────────────────────────────── */
const PartnerRow = ({ p }) => {
  const tone = BAND_TONE[p.band] || BAND_TONE.Stable;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50/70 transition-colors"
         data-testid={`partner-row-${p.partner_key}`}>
      <div className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{ background: tone.bg, color: tone.fg }}>
        <PartnerIcon name={p.icon} size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[12.5px] font-semibold text-slate-900 truncate">{p.name}</p>
          <span aria-hidden className="w-1 h-1 rounded-full bg-slate-300" />
          <p className="text-[10.5px] uppercase tracking-[0.12em] font-bold" style={{ color: tone.fg }}>
            {p.type_label}
          </p>
        </div>
        <p className="text-[11px] text-slate-500 truncate">{p.commentary}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[13px] font-semibold tabular-nums" style={{ color: tone.fg }}>
          {p.composite_score}
        </p>
        <p className="text-[9.5px] uppercase tracking-[0.12em] font-bold" style={{ color: tone.fg }}>
          {p.band}
        </p>
      </div>
      <div className="text-right shrink-0 w-14">
        <p className="text-[11px] font-semibold tabular-nums text-slate-700">
          {p.avg_response_hours != null ? `${p.avg_response_hours}h` : "—"}
        </p>
        <p className="text-[9px] uppercase tracking-[0.12em] font-bold text-slate-500">Avg</p>
      </div>
      <p className="text-[10.5px] tabular-nums text-slate-500 w-10 text-right shrink-0">
        {p.deal_count}d
      </p>
    </div>
  );
};

/* ─── Section header ───────────────────────────────────────────── */
const SubHeader = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-center gap-2 mb-2.5">
    <div className="w-6 h-6 rounded-md inline-flex items-center justify-center"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
      <Icon size={11} strokeWidth={2.3} />
    </div>
    <div className="min-w-0">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-violet-700">{eyebrow}</p>
      <h3 className="text-[12.5px] font-semibold text-slate-900 truncate">{title}</h3>
    </div>
  </div>
);

/* ─── Main Panel ───────────────────────────────────────────────── */
export const PartnerEcosystemPanel = () => {
  const nav = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/partners/ecosystem/overview");
        if (alive) setData(resp?.ecosystem || null);
      } catch (err) {
        if (alive) setError(err?.response?.status === 403
          ? "Partner Ecosystem is leadership-only."
          : "Could not load Partner Ecosystem.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) return null;
  if (loading) return <MissionPanelSkeleton tone="dark" testid="ecosystem-section-loading" />;
  if (!data) return null;

  const partners = data.partners || [];
  const bottlenecks = data.bottlenecks || [];
  const solicitors = data.solicitor_intelligence || {};
  const brokers    = data.broker_intelligence || {};

  return (
    <section className="cf-hydrate-fade mb-7" data-testid="mission-control-partner-ecosystem">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
          style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
          <Network size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
            Partner Ecosystem
          </p>
          <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">
            Multi-party operational coordination across solicitors, brokers, and partners
          </h2>
        </div>
        <button
          type="button"
          onClick={() => nav("/partners")}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[10.5px] font-semibold"
          style={{
            background: "rgba(99,102,241,0.10)", color: "#4338CA",
            border: "1px solid rgba(99,102,241,0.22)",
          }}
          data-testid="ecosystem-open-full"
        >
          Full view
          <ArrowUpRight size={10} strokeWidth={2.4} />
        </button>
      </div>

      <EcosystemHero summary={data.summary} narrative={data.narrative} />

      <div className="gp-card p-4 mb-3" data-testid="ecosystem-partners-list">
        <SubHeader icon={Users} eyebrow="Partner Profiles" title="Live operational responsiveness by partner" />
        <div className="space-y-1">
          {partners.slice(0, 8).map((p) => (
            <PartnerRow key={p.partner_key} p={p} />
          ))}
          {partners.length === 0 ? (
            <p className="text-[11.5px] text-slate-500 italic px-2">
              Partner profiles will appear as solicitors, brokers and other parties accumulate signals.
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div className="gp-card p-4" data-testid="ecosystem-solicitor-intel">
          <SubHeader icon={Scale} eyebrow="Solicitor Intelligence" title="Legal partner performance" />
          <p className="text-[12px] text-slate-600 leading-snug mb-3">{solicitors.narrative}</p>
          {(solicitors.leaders || []).map((s) => (
            <PartnerRow key={s.partner_key} p={s} />
          ))}
        </div>
        <div className="gp-card p-4" data-testid="ecosystem-broker-intel">
          <SubHeader icon={ShieldCheck} eyebrow="Broker Intelligence" title="Mortgage partner performance" />
          <p className="text-[12px] text-slate-600 leading-snug mb-3">{brokers.narrative}</p>
          {(brokers.leaders || []).map((b) => (
            <PartnerRow key={b.partner_key} p={b} />
          ))}
        </div>
      </div>

      {bottlenecks.length ? (
        <div className="gp-card p-4" data-testid="ecosystem-bottlenecks">
          <SubHeader icon={AlertOctagon} eyebrow="Ecosystem Attention" title="Partners with recent operational drift" />
          <div className="space-y-2">
            {bottlenecks.map((b) => (
              <div key={b.partner_key} className="flex items-start gap-2.5 rounded-md p-2"
                style={{ background: "rgba(244,63,94,0.06)" }}
                data-testid={`ecosystem-bottleneck-${b.partner_key}`}>
                <span aria-hidden className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-rose-500" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-slate-900 truncate">
                    {b.name} <span className="text-slate-500">· {b.type_label}</span>
                  </p>
                  <p className="text-[11px] text-slate-600 leading-snug">{b.commentary}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] font-semibold tabular-nums text-rose-700">{b.composite_score}</p>
                  <p className="text-[9.5px] uppercase tracking-[0.12em] font-bold text-rose-700">Drift</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default PartnerEcosystemPanel;
